import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import { Autocomplete, CircularProgress, AutocompleteRenderInputParams, Box, TextField, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLazyRecordValues, RecordOption } from './hooks/useLazyRecordValues';

interface ACKFieldDropdownProps {
    recordTypeId: number;
    fieldTargetId?: string;
    selectedId?: string;
    defaultValue?: string;
    onChange: (value: string | undefined) => void;
    onRecordSelect?: (record: any) => void;
    error?: boolean;
    helperText?: string;
    disabled?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
    onClear?: () => void;
    name?: string;
}

const LISTBOX_HEIGHT = 300;
const THRESHOLD_TO_FIRE_PAGINATED_QUERY = 50;

export const ACKFieldDropdown = ({
    recordTypeId,
    fieldTargetId,
    selectedId,
    defaultValue,
    onChange,
    onRecordSelect,
    error,
    helperText,
    disabled,
    onOpen,
    onClose,
    onClear,
    name
}: ACKFieldDropdownProps) => {
    const {
        open,
        setOpen,
        inputValue,
        setInputValue,
        options: lazyLoadedOptions,
        value,
        setValue,
        allValues,
        isSearching,
        loadingPaginated,
        setPage,
        hasMore,
        getRecordLabel,
        searchByValue
    } = useLazyRecordValues(recordTypeId, fieldTargetId);

    const theme = useTheme();
    const scrollTimeoutRef = useRef<NodeJS.Timeout>();
    const loading = loadingPaginated;

    const displayedOptions = useMemo(
        () => (loading ? [...lazyLoadedOptions, { id: '__loading__', record: null }] : lazyLoadedOptions),
        [lazyLoadedOptions, loading]
    );

    const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
        if (isSearching || allValues) return;
        const listboxNode = event.currentTarget;
        if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - THRESHOLD_TO_FIRE_PAGINATED_QUERY) {
            if (!loadingPaginated && hasMore && !isSearching && !allValues) {
                clearTimeout(scrollTimeoutRef.current);
                scrollTimeoutRef.current = setTimeout(() => {
                    setPage((prev) => prev + 1);
                }, 150); // 150ms debounce
            }
        }
    };

    const handleOpen = () => {
        setOpen(true);
        onOpen?.();
    };

    const handleClose = () => {
        setOpen(false);
        onClose?.();
    };

    const getOptionLabel = useCallback(
        (option: RecordOption) => {
            if (!option.record) return '';
            return getRecordLabel(option.record);
        },
        [getRecordLabel]
    );

    // Extract field value from record (the part after record type name in label)
    const getFieldValue = useCallback(
        (record: any): string => {
            if (!record) return '';

            let fieldValue = record.id; // Default to record.id

            if (fieldTargetId && record?.additionalFields) {
                // Find the field where the last part of tag (split by ;) matches fieldTargetId
                const targetField = record.additionalFields.find((field: any) => {
                    if (!field.tag) return false;
                    const tagParts = field.tag.split(';');
                    const lastPart = tagParts[tagParts.length - 1];
                    return lastPart === fieldTargetId;
                });
                if (targetField) {
                    fieldValue = targetField.value || record.id;
                }
            }

            return fieldValue;
        },
        [fieldTargetId]
    );

    // Set initial value if selectedId is provided (only on mount or when selectedId prop changes)
    const isInitialMount = useRef(true);
    const prevSelectedIdRef = useRef<string | undefined>(undefined);
    const hasSearchedForSelectedId = useRef(false);

    useEffect(() => {
        // Check if selectedId changed from outside (prop change) or if it's initial mount with a selectedId
        const selectedIdChanged = prevSelectedIdRef.current !== selectedId;
        const isInitialWithSelectedId = isInitialMount.current && selectedId;

        if (selectedIdChanged || isInitialWithSelectedId) {
            prevSelectedIdRef.current = selectedId;

            if (!selectedId) {
                if (value && isInitialMount.current) {
                    setValue(null);
                    setInputValue('');
                }
                isInitialMount.current = false;
                return;
            }

            // Try to find the option in current options by field value (not record ID)
            const foundOption = lazyLoadedOptions.find((opt) => {
                if (!opt.record) return false;
                const fieldValue = getFieldValue(opt.record);
                return fieldValue === selectedId;
            });

            if (foundOption && (getFieldValue(value?.record || null) !== selectedId || isInitialMount.current)) {
                // Check if we're in edit mode (selectedId exists and is different from defaultValue)
                const isEditMode = selectedId && selectedId !== defaultValue;
                if (isEditMode) {
                    // Trigger line items loading by calling onRecordSelect with the found record
                    if (foundOption.record && onRecordSelect) {
                        onRecordSelect(foundOption.record);
                    }
                }
                setValue(foundOption);
                setInputValue(getOptionLabel(foundOption));
                isInitialMount.current = false;
                hasSearchedForSelectedId.current = false;
            } else if (!foundOption && selectedId && fieldTargetId) {
                // Option not found in current options - trigger a search to load it
                // Search if selectedId is different from defaultValue (meaning it's a real value from backend)
                // OR if we have no options loaded yet (initial load in edit mode)
                // Only search once per selectedId to avoid infinite loops
                if (!hasSearchedForSelectedId.current && (selectedId !== defaultValue || lazyLoadedOptions.length === 0)) {
                    hasSearchedForSelectedId.current = true;
                    searchByValue(selectedId);
                }
                isInitialMount.current = false;
            } else if (
                !foundOption &&
                lazyLoadedOptions.length > 0 &&
                getFieldValue(value?.record || null) !== selectedId &&
                isInitialMount.current
            ) {
                setValue(null);
                setInputValue('');
                isInitialMount.current = false;
            }
        }
    }, [
        selectedId,
        lazyLoadedOptions,
        value,
        setValue,
        setInputValue,
        getOptionLabel,
        getFieldValue,
        searchByValue,
        fieldTargetId,
        defaultValue,
        onRecordSelect
    ]);

    // Update value when options become available for the selectedId (after search or initial load)
    useEffect(() => {
        if (selectedId && lazyLoadedOptions.length > 0 && getFieldValue(value?.record || null) !== selectedId) {
            const foundOption = lazyLoadedOptions.find((opt) => {
                if (!opt.record) return false;
                const fieldValue = getFieldValue(opt.record);
                return fieldValue === selectedId;
            });
            if (foundOption) {
                // Check if we're in edit mode (selectedId exists and is different from defaultValue)
                const isEditMode = selectedId && selectedId !== defaultValue;
                if (isEditMode) {
                    // Trigger line items loading by calling onRecordSelect with the found record
                    if (foundOption.record && onRecordSelect) {
                        onRecordSelect(foundOption.record);
                    }
                }
                setValue(foundOption);
                setInputValue(getOptionLabel(foundOption));
                hasSearchedForSelectedId.current = false; // Reset so we can search again if selectedId changes
            }
        }
    }, [lazyLoadedOptions, selectedId, value, setValue, setInputValue, getOptionLabel, getFieldValue, defaultValue, onRecordSelect]);

    return (
        <Autocomplete
            data-testid="ack-field-dropdown"
            open={open}
            value={value}
            onChange={(e, newValue, reason) => {
                if (reason === 'clear') onClear?.();
                setValue(newValue);
                // Use field value (part after record type name) instead of record ID
                const fieldValue = newValue?.record ? getFieldValue(newValue.record) : undefined;
                onChange(fieldValue);
                // Call onRecordSelect with the full record when a record is selected
                if (newValue?.record && onRecordSelect) {
                    onRecordSelect(newValue.record);
                }
                if (newValue) {
                    setInputValue(getOptionLabel(newValue));
                } else {
                    setInputValue('');
                }
            }}
            onOpen={handleOpen}
            onClose={handleClose}
            options={displayedOptions.filter((option): option is RecordOption => option?.record !== null && option?.id !== '__loading__')}
            sx={{
                flexGrow: '1 !important',
                height: '2rem',
                '& .MuiFormControl-root': { height: '100%' },
                '& .MuiInputBase-root': { height: '100%', borderRadius: '0.5rem' }
            }}
            getOptionLabel={getOptionLabel}
            inputValue={inputValue}
            onInputChange={(_, newInputValue, reason) => {
                if (reason === 'input') setInputValue(newInputValue);
            }}
            filterOptions={(options) => options}
            ListboxProps={
                {
                    onScroll: handleScroll,
                    style: { maxHeight: LISTBOX_HEIGHT, overflowY: 'auto', paddingBlock: 0 },
                    'data-testid': 'ack-field-dropdown-listbox'
                } as React.HTMLAttributes<HTMLUListElement>
            }
            renderInput={(params: AutocompleteRenderInputParams) => (
                <TextField
                    {...params}
                    name={name}
                    data-testid="ack-field-dropdown-input"
                    variant="filled"
                    size="small"
                    fullWidth
                    disabled={disabled}
                    error={error}
                    helperText={helperText}
                    InputProps={{
                        ...params.InputProps,
                        hiddenLabel: true,
                        disableUnderline: true,
                        placeholder: error ? 'Required Field' : '',
                        sx: {
                            backgroundColor: `${open ? '#EBEEFE' : '#f5f6f7'} !important`,
                            color: '#000',
                            padding: '0 0.8rem !important',
                            height: '2rem',
                            outline: error ? 'solid 1px #EF5350' : 'none',
                            '& .MuiInputBase-input': {
                                maxWidth: '55% !important',
                                overflow: 'hidden !important',
                                textOverflow: 'ellipsis !important',
                                whiteSpace: 'nowrap',
                                display: 'block',
                                backgroundColor: 'inherit !important',
                                color: 'inherit !important'
                            },
                            '& .MuiInputAdornment-root': {
                                '&:hover': { backgroundColor: theme.palette.secondary.light }
                            }
                        }
                    }}
                />
            )}
            renderOption={(_props, option, { selected }) => {
                const optionKey = option.id ?? Math.random().toString();
                if (option?.id === '__loading__' || !option.record) {
                    return (
                        <Box
                            component="li"
                            key={optionKey}
                            {..._props}
                            data-testid="ack-field-dropdown-loading"
                            sx={{ display: 'flex !important', justifyContent: 'center !important', py: 1, backgroundColor: 'white' }}
                        >
                            <CircularProgress size={24} />
                        </Box>
                    );
                }

                const label = getOptionLabel(option);
                return (
                    <Box
                        component="li"
                        {..._props}
                        key={optionKey}
                        data-testid="ack-field-dropdown-option"
                        sx={{
                            border: 'solid 1px',
                            borderColor: theme.palette.primary[500],
                            height: '100%',
                            width: '100%',
                            color: selected ? theme.palette.primary.contrastText : '#54595E',
                            bgcolor: selected ? `${theme.palette.primary.dark} !important` : theme.palette.primary[300],
                            cursor: 'pointer',
                            '&:hover': {
                                bgcolor: `${theme.palette.primary.dark} !important`,
                                color: theme.palette.primary.contrastText
                            }
                        }}
                    >
                        <Tooltip title={label} placement="top" arrow>
                            <Typography
                                noWrap
                                sx={{
                                    flex: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    px: 1
                                }}
                            >
                                {label}
                            </Typography>
                        </Tooltip>
                    </Box>
                );
            }}
        />
    );
};
